import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { Injectable } from '@angular/core';
import { Base } from "./Classes/base";

import { Observable, Subject } from 'rxjs';

import {
    UnitPhotoFactory, WarehouseFactory, ParkingSpaceFactory,
    UnitFactory

} from './Factories';
import {
    UnitsService, UnitsCustomService, UnitFilesService,
    PaginationService
} from '../_services';
import {
    Unit as C, Page, UnitPhoto
} from './Classes';
import { Unit as T, UnitsSummary, Serialized } from './Interfaces';

export const UNIT_INCLUDES =
    'investor,property,warehouses,parking_spaces,contract,insurance,photos';
import { environment } from '../../environments/environment';
@Injectable()
export class Unit extends Base {
    item: C;
    collection: C[];
    photos: UnitPhoto[];
    inPool: number;
    outPool: number;
    total: number;
    totalAreaPoolSum: number;
    page: Page = new Page();
    sanitizer: DomSanitizer;
    changed$: Subject<C> = new Subject<C>();
    constructor(
        private ff: UnitPhotoFactory,
        private factory: UnitFactory,
        private main: UnitsService,
        private custom: UnitsCustomService,
        private fs: UnitFilesService,
        private paging: PaginationService
    ) {
        super();
        if (!this.item) this.make({});

    }
    empty() {
        return !this.item.id;
    }
    get instance() {
        return this.item;
    }
    set instance(value) {
        this.item = value;
        this.changed$.next(this.item);
    }
    isInstance(test) {
        return test instanceof C;
    }
    make(json, summary?: UnitsSummary) {
        this.item = this.factory.createInstance(json, summary);
        this.changed$.next(this.item);
        return this.item;
    }
    makeWithSummary(json) {
        return this.custom.getSummary().map((response: Serialized) => {
            let summary = response.data;
            return this.make(json, summary);
        });
    }
    get(id: number, params?: string): Observable<C> {
        return this.main.get(id, params).map((response: Serialized) => {
            this.item = this.factory.createInstance(response.data, response.meta.summary);
            this.changed$.next(this.item);
            return this.item;
        });
    }
    getAll(params?: string): Observable<C[]> {
        return this.main.getAll(params).map((response: Serialized) => {
            this.inPool = response.meta.summary.in_pool;
            this.total = response.meta.pagination.total;
            this.outPool = this.total - this.inPool;
            this.totalAreaPoolSum = response.meta.summary.all_total_area_pool;
            this.assignPageAndCollection(
                response.meta.pagination, response.data, response.meta.summary);
            return this.collection;
        });
    }

    post(data: any) {
        return this.main.post(data).map((response: Serialized) => {
            this.item = this.factory.createInstance(response.data, response.meta.summary);
            this.changed$.next(this.item);
            return this.item;
        });
    }
    put(id: number, data: any) {
        return this.main.put(id, data).map((response: Serialized) => {
            return response;
        });
    }
    nextPage(next: number, params?: string) {
        return this.paging.next(this.page, params).map((response: Serialized) => {
            this.assignPageAndCollection(
                response.meta.pagination, response.data, response.meta.summary);
            return response;
        });
    }
    prevPage(prev: number, params?: string) {
        return this.paging.prev(this.page, params).map((response: Serialized) => {
            this.assignPageAndCollection(
                response.meta.pagination, response.data, response.meta.summary);
            return response;
        });
    }
    withinPageLimit(next: number) {
        return next < this.page.total_pages;
    }
    search(searchString: string) {
        return this.custom.search(searchString).map((response: Serialized) => {
            this.inPool = response.meta.summary.in_pool;
            this.total = response.meta.pagination.total;
            this.outPool = this.total - this.inPool;
            this.totalAreaPoolSum = response.meta.summary.all_total_area_pool;
            this.assignPageAndCollection(
                response.meta.pagination, response.data, response.meta.summary);
            return this.collection;
        });
    }

    getFiles(id: number, params?: string) {
        return this.fs.getAll(id, params).map((response: Serialized) => {
            let url = `${environment.fileEndpoint}/`;
            this.photos =
                this.ff.createInstances(response.data.map((json) => {
                    json.data = url;
                    return json;
                })).map(file => {
                    if (file.isImage())
                        file.data = this.sanitizer.bypassSecurityTrustUrl(file.data);
                    else {
                        file.data = this.sanitizer.bypassSecurityTrustResourceUrl(file.data);
                    }
                    return file;
                })
            return this.photos;
        });
    }
    deleteFile(unitId: number, fileId: number) {
        return this.fs.delete(unitId, fileId).map((response: Serialized) => {
            return response;
        });
    }
    uploadFile(unitId: number, formData: any) {
        return this.fs.upload(unitId, formData).map((response: Serialized) => {
            return response;
        });
    }
    findWithAttr(array, attr, value) {
        for (var i = 0; i < array.length; i += 1) {
            if (array[i][attr] === value) {
                return i;
            }
        }
        return -1;
    }
    postContract(unitId: number, contractForm) {
        return this.custom.postContract(unitId, contractForm).map((response: Serialized) => {
            return response;
        });
    }
    postInsurance(unitId: number, contractForm) {
        return this.custom.postInsurance(unitId, contractForm).map((response: Serialized) => {
            return response;
        });
    }

    getSummary() {
        return this.custom.getSummary().map((response: Serialized) => {
            let summary = response.data;
            this.inPool = summary.in_pool;
            this.total = summary.total;
            this.outPool = this.total - this.inPool;
            return response;
        });
    }
    calculateAgain(object, offset?: Function) {
        this.item.in_pool = object.in_pool;
        this.item.areas = object;
        let offsetValue: number = (offset) ? offset() : 0;
        this.item.calculate(offsetValue);
    }
    setPage(page, params?: string) {
        let goingPage = page.offset + 1;
        let go: any;
        if (this.page.current_page < goingPage && this.withinPageLimit(page.offset))
            go = this.nextPage(goingPage, params);
        if (this.page.current_page > goingPage && this.withinPageLimit(page.offset))
            go = this.prevPage(goingPage, params);
        if (go) go.subscribe(response => { });
    }
    private assignPageAndCollection(page: Page, collection: T[], summary: UnitsSummary) {
        this.collection = this.factory.createInstances(collection.map(unit => {
            (unit as any).summary = summary;
            return unit;
        }));
        this.page = new Page(page);
    }
}

export const UnitProviders = [
    ParkingSpaceFactory,
    WarehouseFactory,
    UnitPhotoFactory,
    UnitsService,
    UnitsCustomService,
    UnitFilesService,
    PaginationService,
    {
        provide: UnitFactory,
        useClass: UnitFactory,
        deps: [ParkingSpaceFactory, WarehouseFactory, UnitPhotoFactory]
    },
    {
        provide: Unit,
        useClass: Unit,
        deps: [
            UnitPhotoFactory,
            UnitFactory, UnitsService, UnitsCustomService, UnitFilesService, PaginationService
        ]
    }
];



// WEBPACK FOOTER //
// /Users/cmedina/Code/motto_frontend/src/app/_models/unit.ts